import { useEffect, useState, useMemo } from "react";
import { Plus, Search, Loader2, RefreshCcw, KeyRound, Pencil, PenLine } from "lucide-react";
import { UserImagesDialog } from "@/components/UserImagesDialog";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

function genTempPassword() {
  const words = ["Harbor", "Anchor", "Compass", "Horizon", "Mariner", "Ocean", "Trident", "Vessel"];
  const w = words[Math.floor(Math.random() * words.length)];
  const n = Math.floor(100 + Math.random() * 900);
  return `${w}@${n}`;
}

export default function UsersPage() {
  const { hasPermission, user: me } = useAuth();
  const canManage = hasPermission("users.manage");
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const isSuperAdmin = me?.role?.role_name === "Super Admin";
  const [imagesUserId, setImagesUserId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (roleFilter !== "all") params.role_id = roleFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const [u, r] = await Promise.all([
        api.get("/users", { params }),
        api.get("/roles"),
      ]);
      setUsers(u.data);
      setRoles(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter, statusFilter]);

  const onSearchKey = (e) => {
    if (e.key === "Enter") load();
  };

  const openEdit = (u) => {
    setEditing({ ...u });
    setEditOpen(true);
  };

  const toggleStatus = async (u) => {
    try {
      const next = u.status === "active" ? "inactive" : "active";
      await api.patch(`/users/${u.id}`, { status: next });
      toast.success(`User ${next === "active" ? "activated" : "deactivated"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resetTemp = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/reset-temp-password`);
      toast.success(`New temporary password: ${data.temp_password}`, { duration: 10000 });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">User Management</div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-1" style={{ fontFamily: "Manrope" }}>
            Staff & Access
          </h1>
          <div className="text-sm text-slate-500 mt-1">Create accounts, assign roles, control expertise filters.</div>
        </div>

        {canManage && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-user-modal-trigger" className="bg-[#0b192c] hover:bg-[#0f2544] h-10">
                <Plus className="w-4 h-4 mr-1.5" /> New user
              </Button>
            </DialogTrigger>
            <CreateUserDialog
              roles={roles}
              onDone={() => {
                setCreateOpen(false);
                load();
              }}
            />
          </Dialog>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-4 flex flex-wrap items-center gap-3 border-b border-slate-100">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={onSearchKey}
              className="pl-9 h-10"
              data-testid="user-search-input"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-48 h-10" data-testid="user-role-filter-select">
              <SelectValue placeholder="All roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {roles.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.role_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 h-10" data-testid="user-status-filter-select">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} className="h-10" data-testid="user-refresh-btn">
            <RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full crew-table" data-testid="users-table">
            <thead>
              <tr>
                <th className="text-left">Name</th>
                <th className="text-left">Email</th>
                <th className="text-left">Role</th>
                <th className="text-left">Expertise</th>
                <th className="text-left">Status</th>
                <th className="text-left">Created</th>
                {canManage && <th className="text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></td></tr>
              )}
              {!loading && users.length === 0 && (
                <tr><td colSpan={7} className="text-center text-slate-400 py-10">No users found</td></tr>
              )}
              {!loading && users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`}>
                  <td className="font-medium text-slate-900">{u.first_name} {u.last_name}</td>
                  <td className="text-slate-600 font-mono text-[13px]">{u.email}</td>
                  <td className="text-slate-700">{u.role_name || "—"}</td>
                  <td className="text-slate-600">{u.expertise_filter}</td>
                  <td>
                    <span className={`chip ${u.status === "active" ? "chip-success" : "chip-danger"}`}>
                      {u.status}
                    </span>
                    {u.must_change_password && <span className="chip chip-warning ml-1">temp pwd</span>}
                  </td>
                  <td className="text-slate-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  {canManage && (
                    <td className="text-right whitespace-nowrap space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => resetTemp(u)} title="Reset temp password" data-testid={`reset-pwd-${u.email}`}>
                        <KeyRound className="w-3.5 h-3.5" />
                      </Button>
                      {isSuperAdmin && (
                        <Button size="sm" variant="ghost" onClick={() => setImagesUserId(u.id)} title="Signature & stamp" data-testid={`user-images-btn-${u.email}`}>
                          <PenLine className={`w-3.5 h-3.5 ${u.has_signature_image ? "text-cyan-700" : ""}`} />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant={u.status === "active" ? "ghost" : "secondary"}
                        onClick={() => toggleStatus(u)}
                        disabled={u.id === me.id}
                        className={u.status === "active" ? "text-rose-600 hover:text-rose-700" : "text-emerald-700"}
                        data-testid={`toggle-status-${u.email}`}
                      >
                        {u.status === "active" ? "Deactivate" : "Activate"}
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <UserImagesDialog user={users.find((x) => x.id === imagesUserId) || null} onClose={() => setImagesUserId(null)} onChanged={load} />

      {editing && (
        <EditUserDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          user={editing}
          roles={roles}
          onDone={() => {
            setEditOpen(false);
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function CreateUserDialog({ roles, onDone }) {
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [tempPwd, setTempPwd] = useState(genTempPassword());
  const [roleId, setRoleId] = useState("");
  const [expertise, setExpertise] = useState("all");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!roleId) {
      toast.error("Please select a role");
      return;
    }
    setSaving(true);
    try {
      await api.post("/users", {
        first_name: first,
        last_name: last,
        email,
        temp_password: tempPwd,
        role_id: roleId,
        expertise_filter: expertise,
      });
      toast.success(`User created. Temp password: ${tempPwd}`, { duration: 10000 });
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-lg" data-testid="create-user-dialog">
      <DialogHeader>
        <DialogTitle>Create new user</DialogTitle>
        <DialogDescription>They'll be forced to change the temporary password on first login.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>First name</Label>
            <Input value={first} onChange={(e) => setFirst(e.target.value)} required data-testid="create-user-first-name" />
          </div>
          <div>
            <Label>Last name</Label>
            <Input value={last} onChange={(e) => setLast(e.target.value)} required data-testid="create-user-last-name" />
          </div>
        </div>
        <div>
          <Label>Email (login ID)</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="create-user-email" />
        </div>
        <div>
          <Label>Temporary password</Label>
          <div className="flex gap-2">
            <Input value={tempPwd} onChange={(e) => setTempPwd(e.target.value)} required data-testid="create-user-temp-pwd" />
            <Button type="button" variant="outline" onClick={() => setTempPwd(genTempPassword())} data-testid="create-user-regen-pwd">Regenerate</Button>
          </div>
          <div className="text-xs text-slate-500 mt-1">Share this password with the user. They must change it on first login.</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Role</Label>
            <Select value={roleId} onValueChange={setRoleId}>
              <SelectTrigger data-testid="create-user-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
              <SelectContent>
                {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.role_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Expertise filter</Label>
            <Input value={expertise} onChange={(e) => setExpertise(e.target.value)} placeholder="all / rank code / vessel id" data-testid="create-user-expertise" />
          </div>
        </div>
        <DialogFooter>
          <Button type="submit" disabled={saving} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="create-user-form-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create user"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function EditUserDialog({ open, onOpenChange, user, roles, onDone }) {
  const [first, setFirst] = useState(user.first_name);
  const [last, setLast] = useState(user.last_name);
  const [roleId, setRoleId] = useState(user.role_id || "");
  const [expertise, setExpertise] = useState(user.expertise_filter || "all");
  const [status, setStatus] = useState(user.status || "active");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}`, {
        first_name: first,
        last_name: last,
        role_id: roleId,
        expertise_filter: expertise,
        status,
      });
      toast.success("User updated");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
          <DialogDescription>{user.email}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>First name</Label>
              <Input value={first} onChange={(e) => setFirst(e.target.value)} data-testid="edit-user-first-name" />
            </div>
            <div>
              <Label>Last name</Label>
              <Input value={last} onChange={(e) => setLast(e.target.value)} data-testid="edit-user-last-name" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Role</Label>
              <Select value={roleId} onValueChange={setRoleId}>
                <SelectTrigger data-testid="edit-user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.role_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Expertise filter</Label>
              <Input value={expertise} onChange={(e) => setExpertise(e.target.value)} data-testid="edit-user-expertise" />
            </div>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="edit-user-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="edit-user-form-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
